//----- Component --------------------------------------------------------/
// Developer : Daiana Curcio/Nicolas Pascucci
// Creation Date : 05/03/2024
// Version : 2
// Description : Pantalla de carga de pedidos a proveedores por empresa
//------Changes ----------------------------------------------------------//
// Change Date : 04/11/2026
// Change by :
// Change description: Unifica NaturalGas e Hipergas, rediseño y guardado semanal
//========================================================================/

import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import "moment/locale/es";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Navbar from "../Navbar";
import { UrlApi } from "../../services/apirest";
import { logEventos } from "../../services/services";

const EMPRESAS = {
  4: {
    nombre: "NaturalGas",
    ruta: "existentesNatural",
    productos: [
      { clave: "butano", nombre: "Butano", corto: "B" },
      { clave: "propano", nombre: "Propano", corto: "P" },
    ],
  },
  1: {
    nombre: "Hipergas",
    ruta: "existentesHiper",
    productos: [
      { clave: "butano", nombre: "Butano", corto: "B" },
      { clave: "propano", nombre: "Propano", corto: "P" },
      { clave: "bso", nombre: "BSO", corto: "BSO" },
      { clave: "propelente", nombre: "Propelente", corto: "PPTE" },
    ],
  },
};

const COLOR = {
  encabezado: { fondo: "#E3F2FD", borde: "#1976D2" },
  pedido: { fondo: "#E8F5E9", borde: "#2E7D32", celda: "#C8E6C9" },
  resumen: { fondo: "#FFF8E1", borde: "#F9A825" },
};

const panelSx = (color) => ({
  border: "1px solid",
  borderColor: "divider",
  borderTop: `3px solid ${color.borde}`,
  borderRadius: 1,
  bgcolor: color.fondo,
  px: 1.5,
  py: 1,
});

const headerCellSx = {
  py: 0.5,
  px: 1,
  fontSize: 12,
  fontWeight: 700,
  textAlign: "center",
  bgcolor: COLOR.pedido.celda,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  py: 0.5,
  px: 0.5,
  fontSize: 13,
  textAlign: "center",
};

const tituloFilaSx = {
  ...bodyCellSx,
  px: 1,
  textAlign: "left",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const crearSemana = (inicio) =>
  [0, 1, 2, 3, 4, 5, 6].map((dias) => {
    const fecha = inicio.clone().add(dias, "days");

    return {
      fecha: fecha.format("YYYY-MM-DD"),
      nombre: fecha.format("ddd"),
      dia: fecha.format("DD/MM"),
    };
  });

const crearValoresVacios = (semana, productos) =>
  semana.reduce((resultado, dia) => {
    resultado[dia.fecha] = productos.reduce((fila, producto) => {
      fila[producto.clave] = "";
      return fila;
    }, {});
    return resultado;
  }, {});

function PedidosProveedores() {
  const [empresa, setEmpresa] = useState(4);
  const [proveedores, setProveedores] = useState([]);
  const [proveedor, setProveedor] = useState(null);
  const [inicioSemana, setInicioSemana] = useState(() => moment().day(8));
  const [valores, setValores] = useState({});
  const [valoresOriginales, setValoresOriginales] = useState({});
  const [pedidosPorFecha, setPedidosPorFecha] = useState({});
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState("");
  const [errorGuardado, setErrorGuardado] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [cambioPendiente, setCambioPendiente] = useState(null);
  const guardandoRef = useRef(false);

  const configuracion = EMPRESAS[empresa];
  const productos = configuracion.productos;
  const inicioTexto = inicioSemana.format("YYYY-MM-DD");
  const semana = useMemo(() => crearSemana(inicioSemana), [inicioTexto]);
  const periodoTexto =
    inicioSemana.format("DD/MM") +
    " al " +
    inicioSemana.clone().add(6, "days").format("DD/MM/YYYY");

  useEffect(() => {
    logEventos(
      "Acceso al menú pedidos a petroleras",
      "Pedidos a Petroleras",
      "Hacer pedidos a NaturalGas o a Hipergas",
      localStorage.getItem("user")
    );
  }, []);

  useEffect(() => {
    let vigente = true;

    fetch(UrlApi + "/bocas_carga/")
      .then(async (res) => {
        const data = await res.json().catch(() => null);

        if (!res.ok || !Array.isArray(data)) {
          throw new Error("Respuesta inválida al cargar proveedores");
        }

        if (vigente) setProveedores(data);
      })
      .catch((err) => {
        console.error(err);
        if (vigente) setErrorCarga("No se pudo cargar la lista de proveedores.");
      });

    return () => {
      vigente = false;
    };
  }, []);

  useEffect(() => {
    const vacios = crearValoresVacios(semana, productos);

    setErrorGuardado(null);
    setPedidosPorFecha({});
    setValores(vacios);
    setValoresOriginales(vacios);

    if (!proveedor) return undefined;

    let vigente = true;
    setCargando(true);
    setErrorCarga("");

    fetch(
      UrlApi +
      "/proveedores_abastecimiento/" +
      configuracion.ruta +
      "/" +
      proveedor.idbocas_carga +
      "/" +
      semana[0].fecha +
      "/" +
      semana[semana.length - 1].fecha
    )
      .then(async (res) => {
        const data = await res.json().catch(() => null);

        if (!res.ok || !Array.isArray(data)) {
          throw new Error("Respuesta inválida al cargar pedidos");
        }

        if (!vigente) return;

        const nuevosValores = crearValoresVacios(semana, productos);
        const porFecha = {};

        data.forEach((pedido) => {
          const fecha = moment(pedido.fecha).format("YYYY-MM-DD");

          if (!nuevosValores[fecha]) return;

          productos.forEach((producto) => {
            nuevosValores[fecha][producto.clave] =
              pedido[producto.clave] === null
                ? ""
                : String(pedido[producto.clave]);
          });

          porFecha[fecha] = pedido;
        });

        setValores(nuevosValores);
        setValoresOriginales(nuevosValores);
        setPedidosPorFecha(porFecha);
      })
      .catch((err) => {
        console.error(err);
        if (vigente) {
          setErrorCarga(
            "No se pudieron cargar los pedidos de la semana. Recargá la pantalla o avisá a Sistemas."
          );
        }
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, [empresa, proveedor, semana, recarga]);

  const hayCambios = semana.some((dia) =>
    productos.some(
      (producto) =>
        valores[dia.fecha]?.[producto.clave] !==
        valoresOriginales[dia.fecha]?.[producto.clave]
    )
  );

  const ejecutarCambio = (cambio) => {
    if (hayCambios) {
      setCambioPendiente(() => cambio);
    } else {
      cambio();
    }
  };

  const obtenerAsignados = (fecha, producto) => {
    const pedido = pedidosPorFecha[fecha];
    if (!pedido) return 0;

    const asignados =
      (Number(pedido[producto]) || 0) - (Number(pedido[`viaje_${producto}`]) || 0);

    return Math.max(asignados, 0);
  };

  const totalProducto = (producto) =>
    semana.reduce(
      (total, dia) => total + (Number(valores[dia.fecha]?.[producto]) || 0),
      0
    );

  const totalAsignado = (producto) =>
    semana.reduce((total, dia) => total + obtenerAsignados(dia.fecha, producto), 0);

  const cambiarValor = (fecha, producto, valor) => {
    setErrorGuardado(null);
    setValores((anteriores) => ({
      ...anteriores,
      [fecha]: {
        ...anteriores[fecha],
        [producto]: valor.replace(/\D/g, "").slice(0, 3),
      },
    }));
  };

  const guardarSemana = async () => {
    if (guardandoRef.current || !proveedor) return;

    guardandoRef.current = true;
    setGuardando(true);
    setErrorGuardado(null);

    try {
      const res = await fetch(
        UrlApi + "/proveedores_abastecimiento/guardar-semana",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            empresa_destino: empresa,
            proveedor: proveedor.idbocas_carga,
            lunes: semana[0].fecha,
            usuario: localStorage.getItem("user"),
            dias: semana.map((dia) => ({
              fecha: dia.fecha,
              ...valores[dia.fecha],
            })),
          }),
        }
      );

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setErrorGuardado({
          mensaje: data?.mensaje || "No se pudieron guardar los pedidos.",
          problemas: Array.isArray(data?.problemas) ? data.problemas : [],
        });
        return;
      }

      logEventos(
        "Guardar solicitud",
        "Pedidos a Petroleras",
        "Se guardó la semana " +
        semana[0].fecha +
        " de " +
        configuracion.nombre +
        ", proveedor " +
        proveedor.idbocas_carga,
        localStorage.getItem("user")
      );

      setMensaje("Los pedidos se guardaron correctamente.");
      setRecarga((valor) => valor + 1);
    } catch (err) {
      console.error(err);
      setErrorGuardado({
        mensaje: "No se pudo conectar con el servidor. Intentá nuevamente.",
        problemas: [],
      });
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          bgcolor: "grey.50",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Box
          sx={{
            ...panelSx(COLOR.encabezado),
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Pedidos a proveedores</Typography>

          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={empresa}
            sx={{ bgcolor: "white" }}
            onChange={(event, valor) => {
              if (valor !== null && valor !== empresa) {
                ejecutarCambio(() => setEmpresa(valor));
              }
            }}
          >
            <ToggleButton value={4}>NaturalGas</ToggleButton>
            <ToggleButton value={1}>Hipergas</ToggleButton>
          </ToggleButtonGroup>

          <Autocomplete
            size="small"
            disableClearable
            sx={{ width: 320, bgcolor: "white" }}
            options={proveedores}
            value={proveedor}
            autoHighlight
            noOptionsText="Proveedor inexistente"
            isOptionEqualToValue={(opcion, valor) =>
              opcion.idbocas_carga === valor.idbocas_carga
            }
            getOptionLabel={(opcion) =>
              opcion.idbocas_carga +
              " - " +
              opcion.proveedor +
              " - " +
              opcion.nombre_boca
            }
            renderOption={(props, opcion) => (
              <li {...props} key={opcion.idbocas_carga}>
                {opcion.idbocas_carga} - {opcion.proveedor} - {opcion.nombre_boca}
              </li>
            )}
            onChange={(event, valor) =>
              ejecutarCambio(() => setProveedor(valor))
            }
            renderInput={(params) => (
              <TextField {...params} label="Proveedor" />
            )}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Semana anterior">
              <IconButton
                size="small"
                onClick={() =>
                  ejecutarCambio(() =>
                    setInicioSemana((fecha) => fecha.clone().subtract(7, "days"))
                  )
                }
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2">
              Semana: <b>{periodoTexto}</b>
            </Typography>
            <Tooltip title="Semana siguiente">
              <IconButton
                size="small"
                onClick={() =>
                  ejecutarCambio(() =>
                    setInicioSemana((fecha) => fecha.clone().add(7, "days"))
                  )
                }
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {errorCarga ? <Alert severity="error">{errorCarga}</Alert> : null}

        {!proveedor ? (
          <Alert severity="info">
            Elegí un proveedor para ver o cargar los pedidos de {configuracion.nombre}.
          </Alert>
        ) : null}

        <Box sx={panelSx(COLOR.pedido)}>
          <TableContainer sx={{ bgcolor: "white" }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, textAlign: "left" }}>
                    Producto
                  </TableCell>
                  {semana.map((dia) => (
                    <TableCell
                      key={dia.fecha}
                      sx={{ ...headerCellSx, textTransform: "capitalize" }}
                    >
                      {dia.nombre} {dia.dia}
                    </TableCell>
                  ))}
                  <TableCell sx={headerCellSx}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productos.map((producto) => (
                  <TableRow key={producto.clave}>
                    <TableCell sx={tituloFilaSx}>{producto.nombre}</TableCell>
                    {semana.map((dia) => (
                      <TableCell key={dia.fecha} sx={bodyCellSx}>
                        <TextField
                          size="small"
                          value={valores[dia.fecha]?.[producto.clave] ?? ""}
                          disabled={
                            !proveedor || Boolean(errorCarga) || cargando || guardando
                          }
                          onChange={(e) =>
                            cambiarValor(dia.fecha, producto.clave, e.target.value)
                          }
                          inputProps={{
                            inputMode: "numeric",
                            "aria-label": `${producto.nombre} ${dia.dia}`,
                            style: {
                              textAlign: "center",
                              paddingTop: 5,
                              paddingBottom: 5,
                            },
                          }}
                          sx={{ width: 66 }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>
                      {totalProducto(producto.clave)}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell sx={{ ...tituloFilaSx, color: "text.secondary" }}>
                    Asignados
                  </TableCell>
                  {semana.map((dia) => {
                    const texto = productos
                      .map((producto) => ({
                        corto: producto.corto,
                        cantidad: obtenerAsignados(dia.fecha, producto.clave),
                      }))
                      .filter((item) => item.cantidad > 0)
                      .map((item) => `${item.corto} ${item.cantidad}`)
                      .join(" · ");

                    return (
                      <TableCell
                        key={dia.fecha}
                        sx={{ ...bodyCellSx, color: "text.secondary", fontSize: 12 }}
                      >
                        {texto || "-"}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    sx={{ ...bodyCellSx, color: "text.secondary", fontSize: 12 }}
                  >
                    {productos
                      .map(
                        (producto) =>
                          `${producto.corto} ${totalAsignado(producto.clave)}`
                      )
                      .join(" · ")}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box
          sx={{
            ...panelSx(COLOR.resumen),
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 1,
          }}
        >
          <Typography variant="body2">
            Pendientes de asignar:{" "}
            {productos.map((producto, index) => (
              <React.Fragment key={producto.clave}>
                {index > 0 ? " · " : ""}
                <b>
                  {producto.nombre}{" "}
                  {totalProducto(producto.clave) - totalAsignado(producto.clave)}
                </b>
              </React.Fragment>
            ))}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            size="small"
            disabled={!hayCambios || guardando}
            onClick={() => {
              setValores(valoresOriginales);
              setErrorGuardado(null);
            }}
          >
            Deshacer cambios
          </Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={!proveedor || !hayCambios || cargando || guardando}
            onClick={guardarSemana}
          >
            {guardando ? "Guardando..." : "Guardar"}
          </Button>
        </Box>

        {errorGuardado ? (
          <Alert severity="error">
            {errorGuardado.mensaje}
            {errorGuardado.problemas.length > 0 ? (
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                {errorGuardado.problemas.map((problema) => (
                  <li key={problema}>{problema}</li>
                ))}
              </Box>
            ) : null}
          </Alert>
        ) : null}
      </Box>

      <Dialog
        open={Boolean(cambioPendiente)}
        onClose={() => setCambioPendiente(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Tenés cambios sin guardar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Si continuás, se van a perder los cambios que cargaste en esta semana.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCambioPendiente(null)}>Seguir editando</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              cambioPendiente();
              setCambioPendiente(null);
            }}
          >
            Descartar cambios
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(mensaje)}
        autoHideDuration={2500}
        onClose={() => setMensaje("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setMensaje("")}>
          {mensaje}
        </Alert>
      </Snackbar>
    </>
  );
}

export default PedidosProveedores;